import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  VideoCamera, ArrowClockwise, ArrowLeft, Trash, CheckCircle,
  XCircle, Hourglass, Spinner, Download, Copy
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_META = {
  queued:     { icon: Hourglass, label: "Queued",      color: "#EAB308" },
  processing: { icon: Spinner,   label: "Processing",  color: "#00E5FF" },
  retrying:   { icon: Spinner,   label: "Retrying",    color: "#00E5FF" },
  done:       { icon: CheckCircle, label: "Ready",     color: "#22c55e" },
  failed:     { icon: XCircle,   label: "Failed",      color: "#FF3B6B" },
};

export default function BrollHistory() {
  const { setCredits } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/broll");
      setJobs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load B-roll history");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-poll if any job is still in-flight
  useEffect(() => {
    const hasInflight = jobs.some(j => ["queued", "processing", "retrying"].includes(j.status));
    if (!hasInflight) return;
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [jobs, load]);

  const retry = async (job) => {
    setBusy(b => ({ ...b, [job.id]: "retry" }));
    try {
      const { data } = await api.post(`/ai/broll/${job.id}/retry`);
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      toast.success("Retry queued — generating fresh video");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setBusy(b => ({ ...b, [job.id]: null })); }
  };

  const remove = async (job) => {
    if (!window.confirm("Remove this job from history? The video file (if any) stays in storage.")) return;
    setBusy(b => ({ ...b, [job.id]: "delete" }));
    try {
      await api.delete(`/ai/broll/${job.id}`);
      toast.success("Removed");
      setJobs(prev => prev.filter(j => j.id !== job.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(b => ({ ...b, [job.id]: null })); }
  };

  const copyPrompt = (prompt) => {
    navigator.clipboard.writeText(prompt);
    toast.success("Prompt copied");
  };

  const downloadVideo = async (job) => {
    try {
      const { data } = await api.get(`/ai/broll/${job.id}/sign`);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ai/broll/${job.id}/file?sig=${encodeURIComponent(data.sig)}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Could not get download link");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="broll-history-page">
      <Link to="/app/broll" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-4" data-testid="broll-history-back">
        <ArrowLeft size={14} /> Back to B-Roll Studio
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[#FF3B6B] text-sm font-mono">// B-ROLL · HISTORY</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2 flex items-center gap-3">
            <VideoCamera className="text-[#FF3B6B]" /> Generation history
          </h1>
          <p className="text-white/50 mt-1">Every Sora 2 job, including retries and failures.</p>
        </div>
        <button onClick={load} className="glass rounded-lg px-3 py-2 text-sm flex items-center gap-2 hover:bg-white/5" data-testid="broll-history-refresh">
          <ArrowClockwise size={14} /> Refresh
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && jobs.length === 0 && (
          <div className="glass rounded-xl p-12 text-center" data-testid="broll-history-empty">
            <VideoCamera size={48} className="mx-auto mb-3 text-white/20" weight="duotone" />
            <p className="text-white/60">No B-roll generated yet.</p>
            <Link to="/app/broll" className="inline-block mt-4 btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold">
              Create your first B-roll
            </Link>
          </div>
        )}
        {jobs.map(job => {
          const meta = STATUS_META[job.status] || STATUS_META.failed;
          const Icon = meta.icon;
          const isFailed = job.status === "failed";
          const isReady = job.status === "done";
          const isProcessing = ["queued", "processing", "retrying"].includes(job.status);
          return (
            <div key={job.id} className="glass rounded-xl p-5" data-testid={`broll-job-${job.id}`}>
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${meta.color}20`, color: meta.color }}
                >
                  <Icon size={20} weight={isProcessing ? "regular" : "duotone"} className={isProcessing ? "animate-spin" : ""} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: `${meta.color}20`, color: meta.color }}>
                      {meta.label}
                    </span>
                    <span className="text-xs text-white/40">
                      {job.duration}s · {job.size} · {new Date(job.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-white/80 line-clamp-2" title={job.prompt}>{job.prompt}</p>
                  {isFailed && job.error && (
                    <p className="mt-2 text-xs text-[#FF3B6B] bg-[#FF3B6B]/10 rounded px-2 py-1.5" data-testid={`broll-error-${job.id}`}>
                      ⚠ {job.error}
                      {job.credits_refunded ? ` · ${job.credits_refunded} credits refunded` : ""}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                  <button
                    onClick={() => copyPrompt(job.prompt)}
                    className="glass rounded-md p-2 hover:bg-white/5"
                    title="Copy prompt"
                    data-testid={`broll-copy-${job.id}`}
                  >
                    <Copy size={14} />
                  </button>
                  {isReady && (
                    <button
                      onClick={() => downloadVideo(job)}
                      className="glass rounded-md p-2 hover:bg-white/5"
                      title="Download"
                      data-testid={`broll-download-${job.id}`}
                    >
                      <Download size={14} />
                    </button>
                  )}
                  {isFailed && (
                    <button
                      onClick={() => retry(job)}
                      disabled={busy[job.id] === "retry"}
                      className="btn-primary px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50 flex items-center gap-1.5"
                      data-testid={`broll-retry-${job.id}`}
                    >
                      <ArrowClockwise size={12} /> {busy[job.id] === "retry" ? "Retrying…" : "Retry"}
                    </button>
                  )}
                  <button
                    onClick={() => remove(job)}
                    disabled={busy[job.id] === "delete"}
                    className="text-white/40 hover:text-[#FF3B6B] p-2"
                    title="Remove"
                    data-testid={`broll-delete-${job.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
